import React, { useState } from 'react';
import { getConnectionPoints } from '../types/elements';
import { ARCHIMATE_ELEMENTS, getArchimateColors } from '../types/archimateElements';
import { getArchimateIconPath } from '../utils/archimateIcons';

const connectionPointStyles = {
  default: { fill: '#007ACC', stroke: '#ffffff', strokeWidth: 2, radius: 6, opacity: 0.8 },
  hover: { fill: '#00b300', stroke: '#ffffff', strokeWidth: 3, radius: 8, opacity: 1 }
};

/**
 * ArchiMate Element Renderer
 * Рендеринг элементов ArchiMate с поддержкой всех слоёв и типов
 */
const ArchiMateElementRenderer = ({
  element,
  zoom = 1,
  onSelect,
  onDrag,
  onDoubleClick,
  onStartArrowFromConnection,
  showConnectionPoints = true,
  onContextMenu,
  onSelectMultiple,
  onDragMultiple,
  isDraggingElement = false // Передаётся извне для синхронизации состояния
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Получаем определение элемента ArchiMate
  const archimateType = element.archimateType || 'APPLICATION_COMPONENT';
  const elementDef = ARCHIMATE_ELEMENTS[archimateType];
  const layer = elementDef?.layer || 'application';
  const colors = getArchimateColors(layer);
  const iconPath = getArchimateIconPath(elementDef?.type);

  // Точки подключения
  const connectionPoints = getConnectionPoints(element);

  // Обработка начала перетаскивания
  const handleMouseDown = (e) => {
    e.stopPropagation();

    // Обработка множественного выделения
    if (e.ctrlKey || e.metaKey) {
      onSelectMultiple?.(element.id, e);
      return;
    }

    onSelect?.(element.id);

    const svg = e.target.closest('svg');
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const startX = (e.clientX - rect.left) / zoom;
    const startY = (e.clientY - rect.top) / zoom;
    const offsetX = startX - element.x;
    const offsetY = startY - element.y;

    const handleMove = (moveEvt) => {
      const curRect = svg.getBoundingClientRect();
      const x = (moveEvt.clientX - curRect.left) / zoom - offsetX;
      const y = (moveEvt.clientY - curRect.top) / zoom - offsetY;

      // Если элемент выбран вместе с другими, используем множественное перемещение
      if (element.selected && onDragMultiple) {
        onDragMultiple(element.id, x, y);
      } else {
        onDrag?.(element.id, x, y);
      }
    };

    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
  };

  // Создание связи из точки подключения
  const handleConnectionPointMouseDown = (e, side, point) => {
    e.stopPropagation();
    onStartArrowFromConnection?.(element.id, side, point);
  };

  // Рендер точек подключения
  const renderConnectionPoints = () => {
    // Скрываем точки во время перетаскивания
    if (isDraggingElement) return null;
    if (!showConnectionPoints && !element.selected && !isHovered) return null;
    if (!element.selected && !isHovered) return null;

    const mainPoints = ['top', 'right', 'bottom', 'left'];
    const pointsToRender = element.selected
      ? Object.entries(connectionPoints)
      : Object.entries(connectionPoints).filter(([side]) => mainPoints.includes(side));

    return pointsToRender.map(([side, point]) => {
      const isPointHovered = hoveredPoint === side;
      const style = isPointHovered ? connectionPointStyles.hover : connectionPointStyles.default;
      const r = style.radius / zoom;

      return (
        <g key={side}>
          <circle
            cx={point.x}
            cy={point.y}
            r={r * 2.5}
            fill="transparent"
            style={{ cursor: 'crosshair' }}
            onMouseDown={(e) => handleConnectionPointMouseDown(e, side, point)}
            onMouseEnter={() => setHoveredPoint(side)}
            onMouseLeave={() => setHoveredPoint(null)}
          />
          <circle
            cx={point.x}
            cy={point.y}
            r={r}
            fill={style.fill}
            stroke={style.stroke}
            strokeWidth={style.strokeWidth / zoom}
            opacity={style.opacity}
            style={{ cursor: 'crosshair', transition: 'all 0.15s ease-out', pointerEvents: 'none' }}
          />
        </g>
      );
    });
  };

  // Рендер фигуры в зависимости от shape
  const renderShape = () => {
    const { x, y, width, height } = element;
    const shape = elementDef?.shape || 'rectangle';

    switch (shape) {
      case 'rounded_rectangle':
        return (
          <rect
            x={x} y={y} width={width} height={height}
            rx={10} ry={10}
            fill={colors.fill} stroke={colors.stroke} strokeWidth={colors.strokeWidth}
          />
        );

      case 'ellipse':
        return (
          <ellipse
            cx={x + width / 2} cy={y + height / 2}
            rx={width / 2} ry={height / 2}
            fill={colors.fill} stroke={colors.stroke} strokeWidth={colors.strokeWidth}
          />
        );

      case 'cube': {
        // 3D куб для Node, Device, Equipment, Facility
        const depth = Math.min(width, height) * 0.15;
        return (
          <g>
            {/* Боковая грань */}
            <path
              d={`M ${x + width} ${y} L ${x + width + depth} ${y - depth} L ${x + width + depth} ${y + height - depth} L ${x + width} ${y + height} Z`}
              fill={colors.fill} stroke={colors.stroke} strokeWidth={colors.strokeWidth}
              style={{ filter: 'brightness(0.85)' }}
            />
            {/* Верхняя грань */}
            <path
              d={`M ${x} ${y} L ${x + depth} ${y - depth} L ${x + width + depth} ${y - depth} L ${x + width} ${y} Z`}
              fill={colors.fill} stroke={colors.stroke} strokeWidth={colors.strokeWidth}
              style={{ filter: 'brightness(1.1)' }}
            />
            {/* Передняя грань */}
            <rect
              x={x} y={y} width={width} height={height}
              fill={colors.fill} stroke={colors.stroke} strokeWidth={colors.strokeWidth}
            />
          </g>
        );
      }

      case 'rectangle':
      default:
        return (
          <rect
            x={x} y={y} width={width} height={height}
            fill={colors.fill} stroke={colors.stroke} strokeWidth={colors.strokeWidth}
          />
        );
    }
  };

  // Рендер иконки ArchiMate (в правом верхнем углу)
  const renderIcon = () => {
    if (!iconPath) return null;

    const iconSize = 18;
    const iconX = element.x + element.width - iconSize - 4;
    const iconY = element.y + 4;

    return (
      <image
        href={iconPath}
        x={iconX}
        y={iconY}
        width={iconSize}
        height={iconSize}
        style={{ pointerEvents: 'none' }}
      />
    );
  };

  // Рендер текста
  const renderLabel = () => {
    const { x, y, width, height } = element;
    const cx = x + width / 2;
    const cy = y + height / 2;
    const label = element.content || element.label || elementDef?.label || '';

    return (
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={colors.text}
        fontSize={12}
        fontFamily="Arial, sans-serif"
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >
        {label}
      </text>
    );
  };

  const filterStyle = element.selected
    ? 'drop-shadow(0 0 8px #007ACC)'
    : isHovered
      ? 'drop-shadow(0 0 4px rgba(0,122,204,0.5))'
      : 'none';

  return (
    <g
      style={{ cursor: 'move', filter: filterStyle }}
      onMouseDown={handleMouseDown}
      onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick?.(element.id); }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); onContextMenu?.(e, element); }}
    >
      {renderShape()}
      {renderIcon()}
      {renderLabel()}
      {renderConnectionPoints()}
    </g>
  );
};

export default ArchiMateElementRenderer;
